"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from "react";
import Link from 'next/link';
import { ChevronRight, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/button";
import type { NavLink, ContactLink } from "./Header.props";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Nav } from "@/components/atoms/Nav";
import { RawButton } from "@/components/atoms/RawButton";
interface MobileNavProps {
  isOpen: boolean;
  links: NavLink[];
  contactLink?: ContactLink;
  onClose: () => void;
}

/**
 * Checks if the parent has a valid destination URL (not "#" or empty)
 */
function hasValidHref(href: string | undefined): href is string {
  return Boolean(href && href !== "#" && href.trim() !== "");
}

/**
 * Checks if any dropdown item already matches a "View all" pattern
 */
function hasViewAllItem(items: NavLink['dropdownItems']): boolean {
  if (!items) return false;
  return items.some(item => item.title.toLowerCase().startsWith("view all"));
}

/**
 * Creates dropdown items with "View all [Label]" as the first item
 * when the parent has a valid href and no existing "View all" item
 */
function getDropdownItemsWithViewAll(link: NavLink): NonNullable<NavLink['dropdownItems']> {
  const items = link.dropdownItems || [];

  // Only add "View all" if:
  // 1. Parent has a valid destination URL
  // 2. There's no existing "View all" item in the dropdown
  if (hasValidHref(link.href) && !hasViewAllItem(items)) {
    const viewAllItem = {
      title: `View all ${link.label}`,
      href: link.href,
      description: undefined
    };
    return [viewAllItem, ...items];
  }
  return items;
}
export const MobileNav = t0 => {
  const $ = _c(26);
  const {
    isOpen,
    links,
    contactLink,
    onClose
  } = t0;
  const [openDropdown, setOpenDropdown] = useState(null);
  let t1;
  if ($[0] !== onClose) {
    t1 = () => {
      onClose();
      setOpenDropdown(null);
    };
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleLinkClick = t1;
  let t2;
  if ($[2] !== links) {
    t2 = links.map(_temp);
    $[2] = links;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const processedLinks = t2;
  const t3 = !isOpen && "hidden";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("absolute inset-x-0 top-full z-100 flex max-h-[var(--aq-mobile-nav-height)] flex-col overflow-y-auto border-b bg-background p-6 lg:hidden", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== handleLinkClick || $[7] !== openDropdown || $[8] !== processedLinks) {
    let t6;
    if ($[10] !== handleLinkClick || $[11] !== openDropdown) {
      t6 = link_0 => link_0.mobileDropdownItems ? <Box key={link_0.label} className="py-4 first:pt-0 last:pb-0"><RawButton onClick={() => setOpenDropdown(openDropdown === link_0.label ? null : link_0.label)} className="flex w-full items-center justify-between text-base font-medium uppercase text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm" aria-expanded={openDropdown === link_0.label} aria-controls={`mobile-dropdown-${link_0.label.replace(/\s+/g, "-").toLowerCase()}`} aria-label={`${link_0.label} menu, ${openDropdown === link_0.label ? "expanded" : "collapsed"}`}>{link_0.label}<ChevronRight className={cn("size-4 transition-transform duration-200", openDropdown === link_0.label ? "rotate-90" : "")} aria-hidden="true" /></RawButton><Box id={`mobile-dropdown-${link_0.label.replace(/\s+/g, "-").toLowerCase()}`} className="grid transition-[grid-template-rows] duration-300 ease-in-out" style={openDropdown === link_0.label ? {
          gridTemplateRows: "1fr"
        } : {
          gridTemplateRows: "0fr"
        }}><Box className="overflow-hidden"><Box className="mt-4 space-y-3 rounded-lg bg-muted/50 p-4">{link_0.mobileDropdownItems.map((item, index) => item.href === "#group-header" ? <Box key={`${item.title}-${index}`} className={cn("px-2 pt-3 pb-1 text-base font-bold uppercase tracking-wider text-muted-foreground", index > 0 && "border-t border-border mt-2")}>{item.title}</Box> : <Link key={`${item.title}-${index}`} href={item.href} aria-label={item.title} className={cn("group block rounded-md p-2 transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", item.title.toLowerCase().startsWith("view all") && "border-b border-border pb-3 mb-1")} onClick={handleLinkClick}><Box className="transition-transform duration-200 group-hover:translate-x-1"><Box className={cn("font-medium text-primary", item.title.toLowerCase().startsWith("view all") && "font-semibold")}>{item.title}</Box>{item.description && <Text as="p" className="mt-1 text-sm text-muted-foreground">{item.description}</Text>}</Box></Link>)}</Box></Box></Box></Box> : <Link key={link_0.label} href={link_0.href} aria-label={link_0.label} className={cn("py-4 text-base font-medium uppercase text-primary transition-colors first:pt-0 last:pb-0 hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm")} onClick={handleLinkClick}>{link_0.label}</Link>;
      $[10] = handleLinkClick;
      $[11] = openDropdown;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = processedLinks.map(t6);
    $[6] = handleLinkClick;
    $[7] = openDropdown;
    $[8] = processedLinks;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Search className="w-5 h-5" aria-hidden="true" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== handleLinkClick) {
    t7 = <Link href="/search" aria-label="Search" className="py-4 text-base font-medium uppercase text-primary transition-colors flex items-center gap-2 hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm" onClick={handleLinkClick}>{t6}Search</Link>;
    $[14] = handleLinkClick;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <Nav aria-label="Mobile navigation" className="flex flex-1 flex-col divide-y divide-border">{t5}{t7}</Nav>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== contactLink || $[20] !== handleLinkClick) {
    t9 = contactLink && <Box className="mt-6"><Button asChild={true} className="w-full"><Link href={contactLink.href} aria-label={contactLink.label} onClick={handleLinkClick}>{contactLink.label}</Link></Button></Box>;
    $[19] = contactLink;
    $[20] = handleLinkClick;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t4 || $[23] !== t8 || $[24] !== t9) {
    t10 = <Box id="mobile-nav" className={t4}>{t8}{t9}</Box>;
    $[22] = t4;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
function _temp(link) {
  if (link.dropdownGroups) {
    const groupedItems = [];
    if (hasValidHref(link.href)) {
      groupedItems.push({
        title: `View all ${link.label}`,
        href: link.href,
        description: undefined
      });
    }
    link.dropdownGroups.forEach(group => {
      groupedItems.push({
        title: group.label,
        href: "#group-header",
        description: undefined
      });
      groupedItems.push(...group.items);
    });
    return {
      ...link,
      mobileDropdownItems: groupedItems
    };
  }
  return {
    ...link,
    mobileDropdownItems: link.dropdownItems ? getDropdownItemsWithViewAll(link) : undefined
  };
}